import React, {useEffect, useState} from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Container from "@mui/material/Container";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import Tooltip from "@mui/material/Tooltip";
import {NavLink} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {removeFavoriteAsync, markSlideAsViewed} from "../../../shared/model/redux/slides-reducer";
import {HEADERS as SLIDE_HEADERS} from "../../Home/constants";

const HEADERS = [...SLIDE_HEADERS, "Избранное"];
const PAGE_SIZE = 15;

const getInsType = (tissue) => {
    if (!tissue) return 'Отсутствует';
    switch (tissue) {
        case 'colon': return 'Толстая кишка';
        case 'lung': return 'Легкое';
        case 'mc': return 'Молочная железа';
        default: return 'Не определено';
    }
};

const getResultStatus = (resultCode) => {
    switch (resultCode) {
        case 0: return 'Не размечено';
        case 1: return 'Положительное';
        case 2: return 'Сомнительно';
        case 3: return 'Отрицательно';
        default: return 'Не размечено';
    }
};

const getResultStyle = (resultCode) => {
    switch (resultCode) {
        case 0: return {color: '#777', fontStyle: 'italic'};
        case 1: return {color: '#d32f2f', fontWeight: 'bold'};
        case 2: return {color: '#f57c00', fontWeight: 'bold'};
        case 3: return {color: '#388e3c', fontWeight: 'bold'};
        default: return {color: '#666', fontStyle: 'italic'};
    }
};

function FavoriteRow({row, index}) {
    const dispatch = useDispatch();
    const viewedSlides = useSelector((store) => store.slides.viewedSlides);
    const isViewed = viewedSlides.includes(row.id);

    const handleOpenSlide = () => {
        dispatch(markSlideAsViewed(row.id));
    };

    const handleRemoveFavorite = () => {
        dispatch(removeFavoriteAsync(row.id));
    };

    const renderResultCell = () => {
        if (row.result === null) {
            return <span style={{color: '#1976d2', fontStyle: 'italic'}}>Размечается...</span>;
        }
        return <p style={getResultStyle(row.result)}>{getResultStatus(row.result)}</p>;
    };

    return (
        <TableRow className={index % 2 ? '' : 'colored'}>
            <TableCell component="th" scope="row">{getInsType(row.tissue)}</TableCell>
            <TableCell component="th" scope="row">{row.name}</TableCell>
            <TableCell align="center">
                {row.load_date ? new Date(row.load_date).toISOString().slice(0, 19).replace('T', ' ') : ''}
            </TableCell>
            <TableCell align="center">
                <p>{row.status === true ? 'Готово' : 'Не готово'}</p>
            </TableCell>
            <TableCell align="center">
                {row.dzi ? (
                    <NavLink to={`/view/${row.id}/`} target='_blank' onClick={handleOpenSlide}>
                        <button className={'openButton'}>Открыть</button>
                    </NavLink>
                ) : (
                    <span style={{color: '#666', fontStyle: 'italic'}}>Конвертация...</span>
                )}
            </TableCell>
            <TableCell align="center">
                {row.processing_date ? new Date(row.processing_date).toISOString().slice(0, 19).replace('T', ' ') : ''}
            </TableCell>
            <TableCell align="center">{renderResultCell()}</TableCell>
            <TableCell align="center">{row.conclusion ? 'Да' : 'Нет'}</TableCell>
            <TableCell align="center"><p>{isViewed ? 'Да' : 'Нет'}</p></TableCell>
            <TableCell align="center">
                <Tooltip title="Убрать из избранного">
                    <IconButton onClick={handleRemoveFavorite} size="small" color="primary">
                        <BookmarkIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}

function FavoritesTable() {
    const favoriteSlides = useSelector((store) => store.slides.favoriteSlides);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    // Debounce: применяем поиск через 2 сек после остановки ввода
    useEffect(() => {
        const id = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 2000);
        return () => clearTimeout(id);
    }, [searchInput]);

    // Enter — применить поиск сразу
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Enter') {
                setSearch(searchInput);
                setPage(1);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [searchInput]);

    // Сброс страницы при изменении списка избранного
    useEffect(() => {
        setPage(1);
    }, [favoriteSlides.length]);

    const filtered = search
        ? favoriteSlides.filter(s =>
            s.name?.toLowerCase().includes(search.toLowerCase()))
        : favoriteSlides;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <>
            <div style={{width: '500px', margin: '0 20px -25px auto'}}>
                <TextField
                    id="favorites-search"
                    label="Введите имя слайда для поиска"
                    variant="standard"
                    sx={{width: '97%', margin: 1}}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                />
            </div>
            <div>
                <Container sx={{padding: "24px"}} maxWidth={false}>
                    <div style={{
                        height: '72vh',
                        overflow: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                    }}>
                        <Table sx={{minWidth: 650}} aria-label="favorites table" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {HEADERS.map((header, index) => (
                                        <TableCell
                                            key={index}
                                            align="center"
                                            sx={{
                                                backgroundColor: '#f5f5f5',
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid #ddd',
                                            }}
                                        >
                                            <span style={{fontWeight: 'bold'}}>{header}</span>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? (
                                    rows.map((row, index) => (
                                        <FavoriteRow key={row.id} row={row} index={index}/>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={HEADERS.length} align="center" sx={{py: 6, color: '#999'}}>
                                            {search ? 'Ничего не найдено' : 'Нет избранных слайдов'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <Pagination
                        className={'pagination'}
                        sx={{margin: '14px 0', display: 'flex', justifyContent: 'center'}}
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                    />
                </Container>
            </div>
        </>
    );
}

export default FavoritesTable;
